/**
 * URL <-> GlobalFilterState reflection helpers (tech-spec.md §9).
 *
 * Serialization is repeated params with no wrapper, matching the backend
 * wire format used across Risk/Inventory/Detection and the AI pages:
 * `?account_id=a&account_id=b&environment=production`.
 *
 * Writes always go through `history.replaceState` — never `pushState` — so
 * filter changes don't pollute browser history.
 */

export type URLFilterParams = {
  accountId: string[]
  environment: string[]
}

const ACCOUNT_ID_PARAM = 'account_id'
const ENVIRONMENT_PARAM = 'environment'

// @purpose Read account_id/environment repeated params from a URL search string; null when neither key is present so callers can fall back to localStorage
export const readFilterParamsFromURL = (
  search: string,
): URLFilterParams | null => {
  const params = new URLSearchParams(search)
  if (!params.has(ACCOUNT_ID_PARAM) && !params.has(ENVIRONMENT_PARAM)) {
    return null
  }
  return {
    accountId: params.getAll(ACCOUNT_ID_PARAM).filter(v => v !== ''),
    environment: params.getAll(ENVIRONMENT_PARAM).filter(v => v !== ''),
  }
}

// @purpose Mirror the given account_id/environment state into the current URL via history.replaceState (never pushState), preserving unrelated search params
export const writeFilterParamsToURL = (state: URLFilterParams): void => {
  const url = new URL(window.location.href)
  url.searchParams.delete(ACCOUNT_ID_PARAM)
  url.searchParams.delete(ENVIRONMENT_PARAM)
  for (const accountId of state.accountId) {
    url.searchParams.append(ACCOUNT_ID_PARAM, accountId)
  }
  for (const environment of state.environment) {
    url.searchParams.append(ENVIRONMENT_PARAM, environment)
  }
  const next = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState(window.history.state, '', next)
}
